import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { ArrowLeft, ExternalLink, Clock, Sparkles, Play, Copy, Check, Download, Share2, FileDown, Info, Star, Lock, Link2, Globe, MoreHorizontal } from 'lucide-react'
import { api, type VideoDetail, type RelatedVideo, type ShareStatus } from '@/lib/api'
import { captureEvent } from '@/lib/analytics'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { StarButton } from '@/components/star-button'
import { StructuredExtras } from '@/components/structured-extras'

function formatTimestamp(seconds: number): string {
  // Coerce to an integer — legacy rows may contain floats or non-number values
  // from earlier model outputs. Invalid values collapse to 0:00 rather than
  // rendering NaN or fractional seconds.
  const total = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  return `${m}:${s.toString().padStart(2, '0')}`
}

function youtubeTimestampUrl(videoUrl: string, seconds: number): string {
  const base = videoUrl.split('&')[0]
  return `${base}&t=${seconds}s`
}

type KeyPoint = { text: string; timestamp_seconds: number }

function buildSummaryMarkdown(video: VideoDetail, keyPoints: KeyPoint[]): string {
  const lines: string[] = []
  lines.push(`# ${video.title}`)
  lines.push('')
  lines.push(`> **Channel:** ${video.channel_name} · **Source:** [YouTube](${video.youtube_url})`)
  lines.push('')

  if (video.summary_short || video.summary_long) {
    lines.push('## Summary')
    lines.push('')
    if (video.summary_short) {
      lines.push(`**${video.summary_short}**`)
      lines.push('')
    }
    if (video.summary_long) {
      lines.push(video.summary_long)
      lines.push('')
    }
  }

  if (video.claims_json && video.claims_json.length > 0) {
    lines.push('## Claims & arguments')
    lines.push('')
    for (const c of video.claims_json) {
      lines.push(`### ${c.topic}`)
      lines.push('')
      lines.push(`**Claim:** ${c.claim}`)
      lines.push('')
      if (c.supporting_arguments && c.supporting_arguments.length > 0) {
        for (const arg of c.supporting_arguments) {
          if (arg.timestamp_seconds > 0) {
            const ts = formatTimestamp(arg.timestamp_seconds)
            const url = youtubeTimestampUrl(video.youtube_url, arg.timestamp_seconds)
            lines.push(`- [${ts}](${url}) — ${arg.text}`)
          } else {
            lines.push(`- ${arg.text}`)
          }
        }
        lines.push('')
      }
    }
  }

  if (keyPoints.length > 0) {
    lines.push('## Key Points')
    lines.push('')
    for (const p of keyPoints) {
      if (p.timestamp_seconds > 0) {
        const ts = formatTimestamp(p.timestamp_seconds)
        const url = youtubeTimestampUrl(video.youtube_url, p.timestamp_seconds)
        lines.push(`- [${ts}](${url}) — ${p.text}`)
      } else {
        lines.push(`- ${p.text}`)
      }
    }
    lines.push('')
  }

  if (video.worth_watching_if) {
    lines.push('## Worth watching if')
    lines.push('')
    lines.push(video.worth_watching_if)
    lines.push('')
  }

  return lines.join('\n').trimEnd() + '\n'
}

// Kebab-case the title to a safe filename; fall back to the youtube id so we
// never produce an empty name if the title is all punctuation.
function summaryFilename(video: VideoDetail): string {
  const slug = video.title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80)
  return `${slug || video.youtube_video_id}.md`
}

export function VideoPage() {
  const { videoId } = useParams<{ videoId: string }>()
  const [video, setVideo] = useState<VideoDetail | null>(null)
  const [relatedVideos, setRelatedVideos] = useState<RelatedVideo[]>([])
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)
  const [urlCopied, setUrlCopied] = useState(false)

  // "Share to Public" state. null = not yet loaded (or not applicable —
  // status is only fetched for readable summaries).
  const [shareStatus, setShareStatus] = useState<ShareStatus | null>(null)
  const [publicCopied, setPublicCopied] = useState(false)

  // "Share as tweet" dialog: draft text + public back-link, editable before the
  // user opens the X composer. Growth-loop experiment (summary_shared_tweet).
  const [tweetOpen, setTweetOpen] = useState(false)
  const [tweetLoading, setTweetLoading] = useState(false)
  const [tweetText, setTweetText] = useState('')
  const [tweetUrl, setTweetUrl] = useState('')
  const [tweetError, setTweetError] = useState<string | null>(null)

  useEffect(() => {
    if (!videoId) return
    // Reset related state when navigating between videos. Without this,
    // the previous video's related list flashes briefly while the new
    // request is in flight.
    setRelatedVideos([])
    api.getVideo(videoId)
      .then(({ video, relatedVideos }) => {
        setVideo(video)
        setRelatedVideos(relatedVideos ?? [])
        // The value moment: a readable summary actually rendered for the user.
        // Gated on ready + unlocked so locked/quota previews don't inflate it.
        if (video.summary_status === 'ready' && !video.locked) {
          captureEvent('summary_viewed', {
            video_id: video.id,
            content_type: video.content_type,
            source_type: video.source_type,
            confidence_level: video.confidence_level,
          })
          // Share toggle state is only meaningful for readable summaries —
          // the backend rejects share creation for locked/not-ready anyway.
          api.getShareStatus(video.id).then(setShareStatus).catch(() => {})
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [videoId])

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-48 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    )
  }

  if (!video) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Video not found.</p>
        <Link to="/feed"><Button variant="outline" className="mt-4">Back to feed</Button></Link>
      </div>
    )
  }

  // Support both old format (string[]) and new format ({ text, timestamp_seconds }[])
  // Sort by timestamp ascending so key points read in chronological order.
  // Entries without a usable timestamp (0 = legacy string-format or sanitizer-
  // zeroed) fall to the end — their relative order is preserved by the stable
  // sort so the LLM's original sequence is respected among the unknowns.
  const keyPoints: KeyPoint[] = (Array.isArray(video.key_points_json) ? video.key_points_json : [])
    .map(point => typeof point === 'string' ? { text: point, timestamp_seconds: 0 } : point)
    .sort((a, b) => {
      const aHas = a.timestamp_seconds > 0
      const bHas = b.timestamp_seconds > 0
      if (aHas && !bHas) return -1
      if (!aHas && bHas) return 1
      if (!aHas && !bHas) return 0
      return a.timestamp_seconds - b.timestamp_seconds
    })

  async function handleCopy() {
    if (!video) return
    const md = buildSummaryMarkdown(video, keyPoints)
    try {
      await navigator.clipboard.writeText(md)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API can reject in insecure contexts or when the document
      // isn't focused. Fall through silently — the export dropdown still
      // offers Download as a reliable alternative.
    }
  }

  async function handleShare() {
    if (!video) return
    const url = window.location.href
    // Prefer the native share sheet when the browser exposes it (mobile,
    // some desktop browsers) — better UX than copy-to-clipboard. Fall
    // back to clipboard otherwise. AbortError = user dismissed the share
    // sheet; don't fall through to clipboard in that case.
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: video.title, url })
        return
      } catch (err) {
        if ((err as Error).name === 'AbortError') return
      }
    }
    try {
      await navigator.clipboard.writeText(url)
      setUrlCopied(true)
      setTimeout(() => setUrlCopied(false), 2000)
    } catch {
      // Clipboard unavailable (insecure context, no permission). Nothing
      // graceful to do here — the URL is already visible in the address bar.
    }
  }

  async function copyPublicUrl(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      setPublicCopied(true)
      setTimeout(() => setPublicCopied(false), 2000)
    } catch {
      // Clipboard unavailable — the toggle state still flips so the user can
      // retry "Copy public link" once focused/secure.
    }
  }

  // Create (or reuse) the public capability link and copy it. Idempotent on
  // the backend — repeat clicks return the same live link.
  async function handleShareToPublic() {
    if (!video) return
    try {
      const { url } = await api.createShare(video.id)
      setShareStatus({ shared: true, url })
      captureEvent('summary_shared_public', { video_id: video.id })
      await copyPublicUrl(url)
    } catch (err) {
      console.error(err)
    }
  }

  async function handleRevokeShare() {
    if (!video) return
    try {
      await api.revokeShare(video.id)
      setShareStatus({ shared: false, url: null })
    } catch (err) {
      console.error(err)
    }
  }

  // Open the tweet dialog and draft the tweet. Called deferred from the menu
  // (setTimeout) so the dropdown finishes closing before the dialog grabs focus.
  async function openTweetDialog() {
    if (!video) return
    setTweetOpen(true)
    setTweetLoading(true)
    setTweetError(null)
    try {
      const { text, url } = await api.draftTweet(video.id)
      setTweetText(text)
      setTweetUrl(url)
    } catch (err) {
      setTweetError(err instanceof Error ? err.message : 'Could not draft a tweet.')
    } finally {
      setTweetLoading(false)
    }
  }

  // The link is appended via the intent's `url` param so X counts it as a t.co
  // short link rather than eating the 280 from the text.
  const tweetIntentUrl = tweetUrl
    ? `https://x.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(tweetUrl)}`
    : ''
  // Clean label for display — hide the protocol and the utm_* query string.
  const tweetLinkLabel = tweetUrl.replace(/^https?:\/\//, '').replace(/\?.*$/, '')

  function handlePostTweet() {
    if (video) captureEvent('summary_shared_tweet', { video_id: video.id })
    setTweetOpen(false)
  }

  function handleDownload() {
    if (!video) return
    const md = buildSummaryMarkdown(video, keyPoints)
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = summaryFilename(video)
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  function handleOpenInObsidian() {
    if (!video) return
    const md = buildSummaryMarkdown(video, keyPoints)
    // Omit `vault` so Obsidian creates the note in the most recently used
    // vault — we don't know which vault the user wants, and asking would
    // break the one-click flow. If Obsidian isn't installed the browser
    // silently ignores the URI.
    const url = `obsidian://new?name=${encodeURIComponent(video.title)}&content=${encodeURIComponent(md)}`
    window.location.href = url
  }

  return (
    <div className="space-y-6">
      {/* Back */}
      <Link to="/feed" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to feed
      </Link>

      {/* Two-column on wide screens (content + related rail, YouTube-style);
          the rail stacks below on smaller screens. The content column is
          capped at a comfortable reading width when stacked, and the rail is a
          fixed 340px beside it when there's room (xl+). */}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px] xl:items-start">
      <div className="mx-auto w-full max-w-3xl space-y-6 xl:mx-0 xl:max-w-none">

      {/* Hero */}
      <div className="relative overflow-hidden rounded-xl">
        <img
          src={video.thumbnail_url || `https://img.youtube.com/vi/${video.youtube_video_id}/maxresdefault.jpg`}
          alt=""
          className="w-full aspect-video object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <p className="text-sm text-white/80">{video.channel_name}</p>
          <h1 className="mt-1 text-xl font-bold text-white leading-tight">{video.title}</h1>
          <div className="mt-2 flex items-center gap-3 text-xs text-white/70">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDistanceToNow(new Date(video.published_at), { addSuffix: true })}
            </span>
            {video.source_type && (
              <Badge variant="secondary" className="text-xs">
                <Sparkles className="mr-1 h-3 w-3" />
                {video.source_type === 'youtube_video' ? 'Video AI' : 'Transcript'}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Subject tags. Display-only in V1 — clicking does nothing yet.
          Future: filter feed/search by tag. Hidden when the summary hasn't
          produced any (older rows backfilled before tagging shipped). */}
      {video.tags && video.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {video.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded-full border bg-muted/40 px-2.5 py-0.5 text-xs font-medium text-foreground/80"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Summary — full content. When `locked: true` the backend nulls
          summary_short/long, key_points, claims, and structured_extras, so
          we render the LockedSummaryCard instead and skip these cards
          entirely (rendering them with empty data looks broken). */}
      {video.summary_status === 'ready' && !video.locked && (
        <>
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Summary</CardTitle>
              {/* Three compact controls instead of four labeled buttons:
                  Save · Share (link + public-link toggle) · ⋯ (copy/export).
                  Labels hide below sm so the row never squeezes on mobile. */}
              <div className="flex items-center gap-1">
                <StarButton
                  videoId={video.id}
                  initialFavorited={video.is_favorited ?? false}
                  variant="inline"
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Share this summary"
                      className="h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
                    >
                      {urlCopied || publicCopied ? (
                        <>
                          <Check className="h-4 w-4 text-primary" />
                          <span className="hidden text-xs font-medium text-primary sm:inline">Copied</span>
                        </>
                      ) : (
                        <>
                          <Share2 className="h-4 w-4" />
                          <span className="hidden text-xs font-medium sm:inline">Share</span>
                        </>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuItem onSelect={handleShare}>
                      <Link2 className="h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col">
                        <span>Copy link</span>
                        <span className="text-xs text-muted-foreground">Only people on Rundown can view</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => { setTimeout(() => { void openTweetDialog() }, 0) }}>
                      <ExternalLink className="h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col">
                        <span>Share as tweet</span>
                        <span className="text-xs text-muted-foreground">Draft a post with a link back</span>
                      </div>
                    </DropdownMenuItem>
                    {shareStatus?.shared ? (
                      <>
                        <DropdownMenuItem onSelect={() => shareStatus.url && copyPublicUrl(shareStatus.url)}>
                          <Globe className="h-4 w-4 text-primary" />
                          <div className="flex flex-col">
                            <span>Copy public link</span>
                            <span className="text-xs text-muted-foreground">Anyone with the link can view</span>
                          </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={handleRevokeShare}>
                          <Lock className="h-4 w-4 text-muted-foreground" />
                          <div className="flex flex-col">
                            <span>Turn off public link</span>
                            <span className="text-xs text-muted-foreground">The link stops working immediately</span>
                          </div>
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <DropdownMenuItem onSelect={handleShareToPublic}>
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <div className="flex flex-col">
                          <span>Share to public</span>
                          <span className="text-xs text-muted-foreground">Creates a link anyone can open</span>
                        </div>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Dialog open={tweetOpen} onOpenChange={setTweetOpen}>
                  <DialogContent className="w-[calc(100%-2rem)] max-w-lg rounded-xl">
                    <DialogHeader>
                      <DialogTitle>Share as tweet</DialogTitle>
                      <DialogDescription>
                        We drafted the sharpest takeaway. Edit it to your voice, then post — a link back to this summary is added automatically.
                      </DialogDescription>
                    </DialogHeader>
                    {tweetLoading ? (
                      <div className="py-10 text-center text-sm text-muted-foreground">Drafting your tweet…</div>
                    ) : tweetError ? (
                      <div className="space-y-3">
                        <p className="text-sm text-destructive">{tweetError}</p>
                        <Button variant="outline" size="sm" onClick={() => { void openTweetDialog() }}>
                          Try again
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <textarea
                          value={tweetText}
                          onChange={(e) => setTweetText(e.target.value)}
                          rows={4}
                          className="w-full resize-none rounded-lg border border-input bg-muted/30 p-3 text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                        <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                          <span className="flex min-w-0 items-center gap-1.5">
                            <Link2 className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{tweetLinkLabel}</span>
                          </span>
                          <span className={`shrink-0 tabular-nums ${tweetText.length > 250 ? 'text-destructive' : ''}`}>
                            {tweetText.length}/250
                          </span>
                        </div>
                        <a
                          href={tweetIntentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={handlePostTweet}
                          className={`inline-flex h-10 w-full items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 ${
                            tweetText.trim() ? '' : 'pointer-events-none opacity-50'
                          }`}
                        >
                          Post on X
                        </a>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="More actions"
                      className="h-8 px-2 text-muted-foreground hover:text-foreground"
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-primary" />
                      ) : (
                        <MoreHorizontal className="h-4 w-4" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onSelect={handleCopy}>
                      <Copy className="h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col">
                        <span>Copy as Markdown</span>
                        <span className="text-xs text-muted-foreground">Full summary to your clipboard</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={handleDownload}>
                      <FileDown className="h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col">
                        <span>Download as Markdown</span>
                        <span className="text-xs text-muted-foreground">Save a .md file for your vault</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={handleOpenInObsidian}>
                      <Download className="h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col">
                        <span>Open in Obsidian</span>
                        <span className="text-xs text-muted-foreground">Creates a note in your default vault</span>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground/70">
                <Info className="mt-0.5 h-3 w-3 shrink-0" />
                <span>AI summaries can be incomplete or wrong. Verify anything important against the original video.</span>
              </p>
              {video.summary_short && (
                <p className="text-sm font-medium text-primary">{video.summary_short}</p>
              )}
              {video.summary_long && (
                <p className="text-sm leading-relaxed text-foreground/80 whitespace-pre-line break-words">{video.summary_long}</p>
              )}
            </CardContent>
          </Card>

          {/* Content-type-aware structured extras (v2). Renders nothing when
              the row is pre-v2 (NULL extras) or when content_type is one of
              the 6 Phase-2 types that don't carry extras yet. */}
          <StructuredExtras video={video} />

          {/* Claims & arguments — only present for argumentative content
              (education / podcast / debate / news-analysis / history-essay).
              Each claim renders as: TOPIC label (small caps) → claim
              statement (medium-weight body) → indented argument list with
              timestamp pills reusing the Play/timestamp pattern from key
              points. The vertical primary-tinted rule on each claim card
              gives the eye a clear "argument unit" boundary. */}
          {video.claims_json && video.claims_json.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Claims & arguments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {video.claims_json.map((c, i) => (
                  <div key={i} className="space-y-2 border-l-2 border-primary/40 pl-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {c.topic}
                    </p>
                    <p className="text-sm font-medium leading-snug text-foreground">
                      {c.claim}
                    </p>
                    {c.supporting_arguments && c.supporting_arguments.length > 0 && (
                      <ul className="mt-2 space-y-2">
                        {c.supporting_arguments.map((arg, j) => (
                          <li key={j} className="flex items-start gap-3">
                            {arg.timestamp_seconds > 0 ? (
                              <a
                                href={youtubeTimestampUrl(video.youtube_url, arg.timestamp_seconds)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-0.5 shrink-0 inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-mono font-medium text-primary hover:bg-primary/20 transition-colors"
                              >
                                <Play className="h-3 w-3" />
                                {formatTimestamp(arg.timestamp_seconds)}
                              </a>
                            ) : (
                              <span className="mt-1.5 shrink-0 h-1.5 w-1.5 rounded-full bg-primary/40" aria-hidden="true" />
                            )}
                            <span className="text-sm text-foreground/80">{arg.text}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {keyPoints.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Key Points</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {keyPoints.map((point, i) => (
                    <li key={i} className="flex items-start gap-3">
                      {point.timestamp_seconds > 0 ? (
                        <a
                          href={youtubeTimestampUrl(video.youtube_url, point.timestamp_seconds)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-0.5 shrink-0 inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-mono font-medium text-primary hover:bg-primary/20 transition-colors"
                        >
                          <Play className="h-3 w-3" />
                          {formatTimestamp(point.timestamp_seconds)}
                        </a>
                      ) : (
                        // Fallback bullet for key points without a usable
                        // timestamp (legacy string-format rows, or entries the
                        // gemini sanitizer zeroed out). Uses a dot glyph so it
                        // can't be misread as a timestamp like "1" or "9".
                        <span className="mt-0.5 shrink-0 h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                          <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                        </span>
                      )}
                      <span className="text-sm">{point.text}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {video.worth_watching_if && (
            <Card className="border-primary/20 bg-accent/30">
              <CardContent className="p-5">
                <p className="text-sm">
                  <span className="font-semibold text-primary">Worth watching if: </span>
                  {video.worth_watching_if}
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Locked state — the summary exists in the DB but is gated behind
          quota. Without this card the user sees a blank page and reasonably
          assumes the app is broken. lock_reason is 'quota' from this route
          today; we keep a generic fallback in case the union expands. */}
      {video.summary_status === 'ready' && video.locked && (
        <Card className="border-primary/30 bg-accent/20">
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Summary</CardTitle>
            <div className="flex items-center gap-1">
              <StarButton
                videoId={video.id}
                initialFavorited={video.is_favorited ?? false}
                variant="inline"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShare}
                aria-label="Share this summary"
                className="h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
              >
                {urlCopied ? (
                  <>
                    <Check className="h-4 w-4 text-primary" />
                    <span className="text-xs font-medium text-primary">Copied</span>
                  </>
                ) : (
                  <>
                    <Link2 className="h-4 w-4" />
                    <span className="text-xs font-medium">Share</span>
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 py-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Lock className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1.5">
              <h3 className="text-base font-semibold">
                {video.lock_reason === 'quota'
                  ? "You've hit this month's summary limit"
                  : 'This summary is locked'}
              </h3>
              <p className="mx-auto max-w-md text-sm text-muted-foreground">
                {video.lock_reason === 'quota'
                  ? 'Your current plan covers a fixed number of summaries per billing cycle. Upgrade to unlock this video and the rest of this month’s uploads from your followed channels.'
                  : 'Upgrade your plan to unlock this summary.'}
              </p>
            </div>
            <Link to="/upgrade" className="inline-block">
              <Button size="sm" className="gap-1.5">
                <Sparkles className="h-4 w-4" />
                Upgrade plan
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {video.summary_status === 'processing' && (
        <Card className="p-8 text-center">
          <Sparkles className="mx-auto h-8 w-8 text-primary animate-pulse" />
          <p className="mt-2 text-sm text-muted-foreground">AI is analyzing this video...</p>
        </Card>
      )}

      {video.summary_status === 'failed' && (
        <Card className="border-destructive/20 p-8 text-center">
          <p className="text-sm text-destructive">Summary generation failed. It will be retried automatically.</p>
        </Card>
      )}

      {/* CTA */}
      <div className="flex justify-center">
        <a href={video.youtube_url} target="_blank" rel="noopener noreferrer">
          <Button size="lg" variant="outline" className="gap-2">
            <ExternalLink className="h-4 w-4" />
            Watch on YouTube
          </Button>
        </a>
      </div>

      </div>{/* end content column */}

      {/* Related videos — single blended rail, sticky beside the content on
          wide screens, stacked below (capped width) otherwise. Hides cleanly
          when nothing clears the relevance floor. */}
      <aside className="mx-auto w-full max-w-3xl xl:mx-0 xl:max-w-none">
        <div className="xl:sticky xl:top-6">
          <RelatedVideos related={relatedVideos} sourceVideoId={video.id} />
        </div>
      </aside>

      </div>{/* end grid */}
    </div>
  )
}

function RelatedRow({
  r,
  position,
  sourceVideoId,
}: {
  r: RelatedVideo
  position: number
  sourceVideoId: string
}) {
  return (
    <Link
      to={`/videos/${r.id}`}
      onClick={() =>
        captureEvent('related_video_clicked', {
          rail: 'related',
          position,
          source_video_id: sourceVideoId,
          target_video_id: r.id,
          shared_tag_count: r.shared_tags.length,
          is_followed: r.is_followed,
        })
      }
      className="flex items-start gap-3 rounded-lg p-2 transition-colors hover:bg-accent"
    >
      <img
        src={r.thumbnail_url || `https://img.youtube.com/vi/${r.youtube_video_id}/mqdefault.jpg`}
        alt=""
        loading="lazy"
        className="h-14 w-24 shrink-0 rounded-md bg-muted object-cover"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <h4 className="line-clamp-2 flex-1 text-sm font-medium leading-snug">{r.title}</h4>
          {r.is_favorited && (
            <Star
              className="h-3.5 w-3.5 shrink-0 fill-primary text-primary"
              aria-label="In your library"
            />
          )}
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {r.channel_name}
          {/* Subtle "from a creator you follow" marker (skip when favorited —
              the star already signals it's in the library). */}
          {r.is_followed && !r.is_favorited ? ' · Following' : ''} ·{' '}
          {formatDistanceToNow(new Date(r.published_at), { addSuffix: true })}
        </p>
        {/* The "why" behind the match — embeddings rank, tags explain. */}
        {r.shared_tags.length > 0 && (
          <p className="mt-0.5 truncate text-xs text-muted-foreground/80">
            Both cover: {r.shared_tags.slice(0, 3).join(' · ')}
          </p>
        )}
      </div>
    </Link>
  )
}

function RelatedVideos({
  related,
  sourceVideoId,
}: {
  related: RelatedVideo[]
  sourceVideoId: string
}) {
  // Impression: fire once per source video when a non-empty rail renders, so
  // related_video_clicked CTR has a denominator. Keyed on source + count so
  // navigating between videos re-fires.
  useEffect(() => {
    if (related.length === 0) return
    captureEvent('related_rail_shown', {
      rail: 'related',
      source_video_id: sourceVideoId,
      count: related.length,
    })
  }, [sourceVideoId, related.length])

  if (related.length === 0) return null
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Related videos</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {related.map((r, i) => (
          <RelatedRow key={r.id} r={r} position={i} sourceVideoId={sourceVideoId} />
        ))}
      </CardContent>
    </Card>
  )
}
